/**
 * Níveis de qualidade gráfica e a regra que decide quando trocar de um para outro.
 *
 * Tudo aqui é puro e roda sem WebGL: o monitor de quadros (`MonitorQualidade`)
 * só traduz o que mede em eventos `decair` e `subir`, e o redutor decide.
 * O nível controla o que custa GPU de forma proporcional à resolução —
 * densidade de pixels, sombra e os efeitos de pós-processamento.
 */

export type NivelQualidade = 'alta' | 'media' | 'baixa'

export interface EfeitosQualidade {
  /** Oclusão de ambiente (N8AO), ou `null` quando o nível não a permite. */
  readonly oclusaoAmbiente: {
    readonly amostras: number
    readonly amostrasDoFiltro: number
  } | null
  /** Brilho nos materiais emissivos fortes (fogo, faíscas, sinalizadores). */
  readonly bloom: boolean
}

export interface RecursosQualidade {
  /** Faixa de densidade de pixels aceita pelo `<Canvas dpr>`. */
  readonly dpr: [number, number]
  /** Lado do mapa de sombra da luz-chave, em texels. */
  readonly mapaSombra: number
  /**
   * Raio do filtro da sombra, em texels. Cresce junto com o mapa para que a
   * maciez da borda seja a mesma em metros nos três níveis.
   */
  readonly raioSombra: number
  readonly efeitos: EfeitosQualidade
}

export const RECURSOS_QUALIDADE: Readonly<Record<NivelQualidade, RecursosQualidade>> = {
  alta: {
    dpr: [1, 1.8],
    mapaSombra: 2048,
    raioSombra: 5,
    efeitos: { oclusaoAmbiente: { amostras: 12, amostrasDoFiltro: 6 }, bloom: true },
  },
  media: {
    dpr: [1, 1.4],
    mapaSombra: 1024,
    raioSombra: 3,
    efeitos: { oclusaoAmbiente: { amostras: 8, amostrasDoFiltro: 4 }, bloom: true },
  },
  // Sem nenhum efeito permitido a cena vai direto para a tela, com o
  // antialiasing e o tone mapping nativos: o caminho mais barato.
  baixa: {
    dpr: [1, 1],
    mapaSombra: 1024,
    raioSombra: 2,
    efeitos: { oclusaoAmbiente: null, bloom: false },
  },
}

const ORDEM: readonly NivelQualidade[] = ['baixa', 'media', 'alta']

export function nivelAbaixo(nivel: NivelQualidade): NivelQualidade {
  return ORDEM[Math.max(0, ORDEM.indexOf(nivel) - 1)]
}

export function nivelAcima(nivel: NivelQualidade): NivelQualidade {
  return ORDEM[Math.min(ORDEM.length - 1, ORDEM.indexOf(nivel) + 1)]
}

/**
 * Quantas trocas de nível são toleradas antes de a qualidade parar de subir.
 * Sem esse teto, uma máquina que aguenta `alta` por meio minuto e engasga em
 * seguida ficaria alternando entre dois níveis para sempre — e cada troca é
 * um salto visível na nitidez da cena.
 */
export const MAXIMO_TROCAS_ANTES_DE_TRAVAR = 3

export interface EstadoQualidade {
  readonly nivel: NivelQualidade
  readonly trocas: number
}

export type EventoQualidade = 'decair' | 'subir'

export const ESTADO_QUALIDADE_INICIAL: EstadoQualidade = { nivel: 'alta', trocas: 0 }

/**
 * Descer é sempre permitido: é o que protege a aula de uma cena travada.
 * Subir só enquanto o orçamento de trocas não acabou; depois disso a
 * qualidade só pode cair, e o nível onde ela assenta é o que a máquina aguenta.
 * Evento que não muda o nível (já no topo ou no piso) não gasta troca.
 */
export function reduzirQualidade(estado: EstadoQualidade, evento: EventoQualidade): EstadoQualidade {
  if (evento === 'subir' && estado.trocas >= MAXIMO_TROCAS_ANTES_DE_TRAVAR) return estado

  const nivel = evento === 'decair' ? nivelAbaixo(estado.nivel) : nivelAcima(estado.nivel)
  if (nivel === estado.nivel) return estado

  return { nivel, trocas: estado.trocas + 1 }
}

/**
 * Permite fixar o nível por endereço (`?qualidade=baixa`), para comparar
 * visuais e depurar sem depender da máquina. Valor desconhecido é ignorado.
 */
export function nivelForcadoDaBusca(busca: string): NivelQualidade | null {
  const valor = new URLSearchParams(busca).get('qualidade')
  return valor === 'alta' || valor === 'media' || valor === 'baixa' ? valor : null
}

export interface PedidosDaCena {
  /** A cena é predominantemente opaca, onde a oclusão de ambiente ajuda. */
  readonly oclusaoAmbiente: boolean
  /** Quantos emissores fortes (fogo, faíscas) estão ativos. */
  readonly emissores: number
  /** Há peça contornada. */
  readonly contorno: boolean
}

export interface DecisaoPosProcessamento {
  readonly composer: boolean
  readonly oclusaoAmbiente: NonNullable<EfeitosQualidade['oclusaoAmbiente']> | null
  readonly bloom: boolean
}

/**
 * Oclusão de ambiente e brilho são opt-in (`?efeitos=extras`), não padrão: o
 * composer mistura transparências em luz linear, e nas zonas translúcidas do
 * Table top isso deslocou o matiz de cores que significam algo (medido em um
 * ponto de cada zona: a película azul-esverdeada de 181° para 226°, o
 * preenchimento laranja de 22° para 14°).
 * O ganho da oclusão, medido, é sutil. Fica disponível para avaliar em hardware
 * real, mas só liga quando pedido.
 */
export function efeitosExtrasDaBusca(busca: string): boolean {
  return new URLSearchParams(busca).get('efeitos') === 'extras'
}

export const SEM_EFEITOS: EfeitosQualidade = { oclusaoAmbiente: null, bloom: false }

/**
 * O nível de qualidade diz o que é permitido; a cena, o que ela pede.
 *
 * O composer redireciona a cena inteira para um buffer de ponto flutuante e a
 * refaz em passes de tela cheia. Além do custo, ele mistura transparências em
 * luz linear em vez de em sRGB, e as superfícies translúcidas — o recurso
 * didático das vistas em camadas — saem mais claras e leitosas. Por isso só
 * entra quando algo pede e o nível permite; no resto do tempo a cena vai direto
 * para a tela, com o antialiasing e o tone mapping nativos.
 */
export function decidirPosProcessamento(
  permitidos: EfeitosQualidade,
  pedidos: PedidosDaCena,
): DecisaoPosProcessamento {
  const oclusaoAmbiente = pedidos.oclusaoAmbiente ? permitidos.oclusaoAmbiente : null
  const bloom = permitidos.bloom && pedidos.emissores > 0
  return {
    composer: pedidos.contorno || oclusaoAmbiente !== null || bloom,
    oclusaoAmbiente,
    bloom,
  }
}
